import { BsEye } from 'react-icons/bs';
import DocumentLinesModal from '../components/documents/DocumentLinesModal';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';
import { formatCurrency, formatDate, downloadFile, previewFile } from '../utils/invoiceUtils';
import BudgetKpiCards from '../components/budgets/BudgetKpiCards';

const OPEN_STATES = ['Borrador', 'Enviado', 'Pendiente'];

// Color Bootstrap por estado de presupuesto
const statusBadge = (estado) => {
  const map = {
    Borrador: 'bg-secondary',
    Enviado: 'bg-info text-dark',
    Pendiente: 'bg-warning text-dark',
    Aceptado: 'bg-success',
    Rechazado: 'bg-danger',
    Caducado: 'bg-dark'
  };
  return map[estado] || 'bg-secondary';
};

// Estado efectivo (deriva "Caducado" cuando un presupuesto abierto supera su validez)
const effectiveStatus = (b) => {
  if (OPEN_STATES.includes(b.estado) && b.valid_until) {
    const d = new Date(b.valid_until);
    if (!Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString())) return 'Caducado';
  }
  return b.estado;
};

const PresupuestosPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', status: '', clientId: '', canal: '', startDate: '', endDate: '' });
  const [kpis, setKpis] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [settings, setSettings] = useState({ series: '', seriesPad: 4, validDays: 30 });
  const [savingSettings, setSavingSettings] = useState(false);
  const [filterClientOptions, setFilterClientOptions] = useState([]);
  const [filterClientQuery, setFilterClientQuery] = useState('');
  const [filterClientsLoading, setFilterClientsLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [linesDocument, setLinesDocument] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailModal, setEmailModal] = useState({ open: false, sending: false, error: '', budgetId: null, to: '', subject: '', templateId: '', bodyHtml: '', budgetNumber: '' });
  const [whatsappModal, setWhatsappModal] = useState({ open: false, loading: false, sending: false, error: '', budgetId: null, phone: '', clientName: '', budgetNumber: '' });

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const fetchBudgets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.canal) params.append('canal', filters.canal);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      const res = await apiFetch(`/api/budgets/all?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error obteniendo presupuestos');
      if (Array.isArray(data)) {
        setBudgets(data);
        setTotal(data.length);
      } else {
        setBudgets(Array.isArray(data.items) ? data.items : []);
        setTotal(Number(data.total || 0));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, token]);

  const loadKpis = useCallback(async () => {
    try {
      const res = await apiFetch('/api/budgets/kpis', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok) setKpis(data);
    } catch (_) { /* noop */ }
  }, [token]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await apiFetch('/api/budgets/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setSettings({ series: data?.series || '', seriesPad: data?.seriesPad || 4, validDays: data?.validDays ?? 30 });
      }
    } catch (err) {
      console.error('Error cargando configuración de presupuestos', err);
    }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/email/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.ok) setTemplates(Array.isArray(data) ? data : []);
    } catch (_) { /* noop */ }
  }, [token]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await apiFetch('/api/budgets/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setSettings({ series: data?.series || '', seriesPad: data?.seriesPad || settings.seriesPad, validDays: data?.validDays ?? settings.validDays });
      setConfigOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchClientsForFilter = useCallback(async (search = '') => {
    setFilterClientsLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, pageSize: 20 });
      if (search) params.append('search', search);
      const res = await apiFetch(`/api/clients?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando clientes');
      setFilterClientOptions((data?.data || []).map((c) => ({
        value: c.id,
        label: `${c.razon_social || `Cliente #${c.id}`}${c.email ? ` · ${c.email}` : ''}`
      })));
    } catch (err) {
      console.error(err.message);
    } finally {
      setFilterClientsLoading(false);
    }
  }, [token]);

  const handleDownloadFile = async (endpoint, filename) => {
    try {
      await downloadFile(endpoint, filename, token, handleUnauthorized);
    } catch (err) {
      alert(err.message);
    }
  };

  const handlePreviewFile = async (endpoint) => {
    try {
      await previewFile(endpoint, token, handleUnauthorized);
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleVisibility = async (budget) => {
    const nuevoValor = !(budget.visible_cliente !== false);
    setBudgets((prev) => prev.map((b) => (b.id === budget.id ? { ...b, visible_cliente: nuevoValor } : b)));
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ visible_cliente: nuevoValor })
      });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) {
        const data = await parseJsonSafe(res);
        throw new Error((data && data.message) || 'No se pudo actualizar la visibilidad');
      }
    } catch (err) {
      setBudgets((prev) => prev.map((b) => (b.id === budget.id ? { ...b, visible_cliente: !nuevoValor } : b)));
      alert(err.message);
    }
  };

  const changeStatus = async (budget, estado) => {
    if (actingId) return;
    setOpenMenuId(null);
    setActingId(`${budget.id}:estado`);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ estado })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo cambiar el estado');
      setBudgets((prev) => prev.map((b) => (b.id === budget.id ? { ...b, estado: data.estado } : b)));
      loadKpis();
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const duplicateBudget = async (budget) => {
    if (actingId) return;
    setOpenMenuId(null);
    setActingId(`${budget.id}:duplicar`);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/duplicate`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo duplicar');
      navigate(`/presupuestos/${data.id}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const convertToInvoice = async (budget) => {
    if (actingId) return;
    setOpenMenuId(null);
    if (!window.confirm('¿Convertir este presupuesto en factura (borrador)?')) return;
    setActingId(`${budget.id}:convertir`);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}/convert-to-invoice`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo convertir');
      alert(`Factura creada: ${data.numero || ''}`);
      navigate(`/facturas/${data.id}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  const deleteBudget = async (budget) => {
    if (actingId) return;
    setOpenMenuId(null);
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    setActingId(`${budget.id}:eliminar`);
    try {
      const res = await apiFetch(`/api/clients/budgets/${budget.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error eliminando presupuesto');
      fetchBudgets();
      loadKpis();
    } catch (err) {
      alert(err.message);
    } finally {
      setActingId(null);
    }
  };

  // --- Email ---
  const openEmailModal = async (budget) => {
    setOpenMenuId(null);
    const baseNumber = budget?.presupuesto || `#${budget.id}`;
    const defaultTemplateId = templates[0]?.id ? String(templates[0].id) : '';
    const template = templates.find((t) => String(t.id) === defaultTemplateId);
    setEmailModal({
      open: true, sending: false, error: '', budgetId: budget.id, to: '',
      subject: `Presupuesto ${baseNumber}`, templateId: defaultTemplateId,
      bodyHtml: template?.body_html || '', budgetNumber: baseNumber
    });
    if (budget.client_id) {
      try {
        const res = await apiFetch(`/api/clients/${budget.client_id}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await parseJsonSafe(res);
        if (res.ok) setEmailModal((prev) => ({ ...prev, to: data.email || prev.to }));
      } catch (_) { /* noop */ }
    }
  };

  const sendEmail = async () => {
    if (!emailModal.budgetId) return;
    if (!emailModal.to.trim()) return setEmailModal((p) => ({ ...p, error: 'El email es obligatorio.' }));
    setEmailModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${emailModal.budgetId}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: emailModal.to, subject: emailModal.subject, html: emailModal.bodyHtml, templateId: emailModal.templateId || undefined })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por email');
      setEmailModal((p) => ({ ...p, sending: false, open: false }));
      fetchBudgets();
      loadKpis();
    } catch (err) {
      setEmailModal((p) => ({ ...p, sending: false, error: err.message }));
    }
  };

  // --- WhatsApp ---
  const openWhatsappModal = async (budget) => {
    setOpenMenuId(null);
    setWhatsappModal({ open: true, loading: true, sending: false, error: '', budgetId: budget.id, phone: '', clientName: budget.razon_social || '', budgetNumber: budget.presupuesto || `#${budget.id}` });
    if (!budget.client_id) {
      setWhatsappModal((p) => ({ ...p, loading: false, error: 'Presupuesto sin cliente asociado.' }));
      return;
    }
    try {
      const res = await apiFetch(`/api/clients/${budget.client_id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando cliente');
      setWhatsappModal((p) => ({ ...p, loading: false, phone: data.telefono || '', clientName: data.razon_social || p.clientName }));
    } catch (err) {
      setWhatsappModal((p) => ({ ...p, loading: false, error: err.message }));
    }
  };

  const sendWhatsapp = async () => {
    if (!whatsappModal.budgetId) return;
    if (!whatsappModal.phone.trim()) return setWhatsappModal((p) => ({ ...p, error: 'El teléfono es obligatorio.' }));
    setWhatsappModal((p) => ({ ...p, sending: true, error: '' }));
    try {
      const res = await apiFetch(`/api/clients/budgets/${whatsappModal.budgetId}/send-whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo enviar por WhatsApp');
      setWhatsappModal((p) => ({ ...p, sending: false, open: false }));
      fetchBudgets();
      loadKpis();
    } catch (err) {
      setWhatsappModal((p) => ({ ...p, sending: false, error: err.message }));
    }
  };

  const selectedFilterClient = useMemo(() => {
    if (!filters.clientId) return null;
    const parsed = Number(filters.clientId);
    return filterClientOptions.find((opt) => opt.value === parsed) || { value: parsed, label: `Cliente #${filters.clientId}` };
  }, [filters.clientId, filterClientOptions]);

  useEffect(() => { fetchBudgets(); }, [fetchBudgets]);
  useEffect(() => { loadKpis(); loadSettings(); loadTemplates(); }, [loadKpis, loadSettings, loadTemplates]);

  useEffect(() => {
    const q = filterClientQuery.trim();
    const handler = setTimeout(() => fetchClientsForFilter(q), 250);
    return () => clearTimeout(handler);
  }, [filterClientQuery, fetchClientsForFilter]);

  useEffect(() => {
    if (openMenuId == null) return undefined;
    const onClick = (e) => { if (!e.target.closest('.budget-actions-menu')) setOpenMenuId(null); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [openMenuId]);

  return (
    <div className="small">
      {linesDocument && <DocumentLinesModal document={linesDocument} kind="budgets" onClose={() => setLinesDocument(null)} />}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Presupuestos</h2>
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/presupuestos/nuevo')}>Nuevo presupuesto</button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfigOpen(true)}>Configuración</button>
          {loading && <span className="badge bg-info text-dark">Cargando...</span>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <BudgetKpiCards
        kpis={kpis}
        onClickOpen={() => { setPage(1); setFilters((p) => ({ ...p, status: 'Abiertos' })); }}
        onClickAccepted={() => { setPage(1); setFilters((p) => ({ ...p, status: 'Aceptado' })); }}
        onClickExpired={() => { setPage(1); setFilters((p) => ({ ...p, status: 'Caducado' })); }}
      />

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Todos los presupuestos</h5>
            <div className="text-muted small">Total: {total}</div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Buscar por número, cliente o email"
                value={filters.search}
                onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, search: e.target.value })); }}
              />
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={filters.status} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, status: e.target.value })); }}>
                <option value="">Todos los estados</option>
                <option value="Abiertos">Abiertos (en curso)</option>
                <option value="Borrador">Borrador</option>
                <option value="Enviado">Enviado</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Aceptado">Aceptado</option>
                <option value="Rechazado">Rechazado</option>
                <option value="Caducado">Caducado</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={filters.canal} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, canal: e.target.value })); }}>
                <option value="">Todos los orígenes</option>
                <option value="Manual">Manual</option>
                <option value="Dolibarr">Dolibarr</option>
              </select>
            </div>
            <div className="col-md-4">
              <Select
                classNamePrefix="select"
                isLoading={filterClientsLoading}
                options={filterClientOptions}
                value={selectedFilterClient}
                placeholder="Cliente"
                onChange={(opt) => { setPage(1); setFilters((p) => ({ ...p, clientId: opt ? String(opt.value) : '' })); }}
                onInputChange={(val, action) => { if (action.action === 'input-change') setFilterClientQuery(val); return val; }}
                isClearable
                noOptionsMessage={() => 'Sin resultados'}
              />
            </div>
          </div>
          <div className="row g-2 mb-3">
            <div className="col-md-3">
              <input className="form-control form-control-sm" type="date" value={filters.startDate} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, startDate: e.target.value })); }} />
            </div>
            <div className="col-md-3">
              <input className="form-control form-control-sm" type="date" value={filters.endDate} onChange={(e) => { setPage(1); setFilters((p) => ({ ...p, endDate: e.target.value })); }} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary btn-sm w-100" type="button" onClick={() => { setFilters({ search: '', status: '', clientId: '', canal: '', startDate: '', endDate: '' }); setPage(1); }}>Limpiar</button>
            </div>
          </div>

          {loading ? (
            <p>Cargando...</p>
          ) : budgets.length ? (
            <div className="table-responsive">
              <table className="table table-striped table-sm align-middle">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Presupuesto</th>
                    <th>Fecha</th>
                    <th>Validez</th>
                    <th>Estado</th>
                    <th className="text-end">Subtotal</th><th className="text-end">Total con IVA</th>
                    <th className="text-center" title="Visible en el área cliente">Área cliente</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map((b) => {
                    const est = effectiveStatus(b);
                    const isDolibarr = Boolean(b.dolibarr_proposal_id);
                    const busy = actingId && actingId.startsWith(`${b.id}:`);
                    return (
                      <tr key={b.id}>
                        <td>
                          {b.client_id ? (
                            <Link to={`/clientes/${b.client_id}`} className="text-decoration-none">{b.razon_social || `Cliente #${b.client_id}`}</Link>
                          ) : (b.razon_social || '-')}
                        </td>
                        <td>{b.presupuesto}</td>
                        <td>{formatDate(b.fecha)}</td>
                        <td>{b.valid_until ? formatDate(b.valid_until) : '-'}</td>
                        <td>
                          <span className={`badge ${statusBadge(est)}`}>{est}</span>
                          {b.converted && <span className="badge bg-success-subtle text-success ms-1" title="Convertido a factura">Facturado</span>}
                        </td>
                        <td className="text-end">{formatCurrency(b.base_imponible)}</td><td className="text-end">{formatCurrency(b.total)}</td>
                        <td className="text-center">
                          <div className="form-check form-switch d-inline-flex justify-content-center m-0" title={b.visible_cliente !== false ? 'Visible en el área cliente' : 'Oculto en el área cliente'}>
                            <input className="form-check-input" type="checkbox" role="switch" checked={b.visible_cliente !== false} onChange={() => toggleVisibility(b)} aria-label="Visible en área cliente" />
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm budget-actions-menu" role="group" style={{ position: 'relative' }}>
                            <button type="button" className="btn btn-outline-secondary" title="Ver líneas" aria-label={`Ver líneas de presupuesto ${b.presupuesto || b.id}`} onClick={() => { setOpenMenuId(null); setLinesDocument(b); }}><BsEye aria-hidden="true" /></button>
                            <button className="btn btn-outline-secondary" type="button" onClick={() => navigate(`/presupuestos/${b.id}`)}>{isDolibarr ? 'Ver' : 'Editar'}</button>
                            <button className="btn btn-outline-secondary dropdown-toggle" type="button" aria-expanded={openMenuId === b.id} disabled={busy} onClick={() => setOpenMenuId((prev) => (prev === b.id ? null : b.id))}>
                              {busy ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Acciones'}
                            </button>
                            <ul className={`dropdown-menu dropdown-menu-end${openMenuId === b.id ? ' show' : ''}`} style={{ position: 'absolute', right: 0, top: '100%' }}>
                              <li><button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handlePreviewFile(`/api/clients/budgets/${b.id}/${isDolibarr ? 'dolibarr-pdf' : 'pdf'}`); }}>Previsualizar PDF</button></li>
                              <li><button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handleDownloadFile(`/api/clients/budgets/${b.id}/pdf`, `${b.presupuesto || 'presupuesto'}.pdf`); }}>Descargar PDF</button></li>
                              <li><button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handleDownloadFile(`/api/clients/budgets/${b.id}/pptx`, `${b.presupuesto || 'presupuesto'}.pptx`); }}>Descargar PPTX</button></li>
                              {isDolibarr && <li><button className="dropdown-item" type="button" onClick={() => { setOpenMenuId(null); handleDownloadFile(`/api/clients/budgets/${b.id}/dolibarr-pdf`, `${b.presupuesto || 'presupuesto'}.pdf`); }}>PDF Dolibarr</button></li>}
                              <li><button className="dropdown-item" type="button" onClick={() => openEmailModal(b)}>Enviar por email</button></li>
                              <li><button className="dropdown-item" type="button" onClick={() => openWhatsappModal(b)}>Enviar por WhatsApp</button></li>
                              <li><hr className="dropdown-divider" /></li>
                              {!isDolibarr && est !== 'Aceptado' && <li><button className="dropdown-item text-success" type="button" onClick={() => changeStatus(b, 'Aceptado')}>Marcar aceptado</button></li>}
                              {!isDolibarr && est !== 'Rechazado' && <li><button className="dropdown-item text-danger" type="button" onClick={() => changeStatus(b, 'Rechazado')}>Marcar rechazado</button></li>}
                              {!isDolibarr && est === 'Borrador' && <li><button className="dropdown-item" type="button" onClick={() => changeStatus(b, 'Enviado')}>Marcar enviado</button></li>}
                              <li><button className="dropdown-item" type="button" onClick={() => duplicateBudget(b)}>Duplicar</button></li>
                              {!b.converted && <li><button className="dropdown-item" type="button" onClick={() => convertToInvoice(b)}>Convertir a factura</button></li>}
                              {!isDolibarr && (
                                <>
                                  <li><hr className="dropdown-divider" /></li>
                                  <li><button className="dropdown-item text-danger" type="button" onClick={() => deleteBudget(b)}>Eliminar</button></li>
                                </>
                              )}
                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">No hay presupuestos con los filtros actuales.</p>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {configOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración de presupuestos</h5>
                <button className="btn-close" onClick={() => setConfigOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small">Serie de presupuestos</label>
                    <input className="form-control" value={settings.series} onChange={(e) => setSettings((p) => ({ ...p, series: e.target.value }))} placeholder="PR" />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Dígitos</label>
                    <input className="form-control" type="number" min="1" max="8" value={settings.seriesPad} onChange={(e) => setSettings((p) => ({ ...p, seriesPad: Number(e.target.value || 1) }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Validez (días)</label>
                    <input className="form-control" type="number" min="0" max="365" value={settings.validDays} onChange={(e) => setSettings((p) => ({ ...p, validDays: Number(e.target.value || 0) }))} />
                  </div>
                </div>
                <div className="text-muted small mt-2">La validez por defecto se usa para calcular "Válido hasta" al crear un presupuesto.</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setConfigOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>{savingSettings ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {emailModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar presupuesto {emailModal.budgetNumber} por email</h5>
                <button className="btn-close" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {emailModal.error && <div className="alert alert-danger py-2">{emailModal.error}</div>}
                <div className="mb-2">
                  <label className="form-label small">Para</label>
                  <input className="form-control" value={emailModal.to} onChange={(e) => setEmailModal((p) => ({ ...p, to: e.target.value }))} placeholder="cliente@email.com" />
                </div>
                <div className="mb-2">
                  <label className="form-label small">Asunto</label>
                  <input className="form-control" value={emailModal.subject} onChange={(e) => setEmailModal((p) => ({ ...p, subject: e.target.value }))} />
                </div>
                {templates.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label small">Plantilla</label>
                    <select className="form-select" value={emailModal.templateId} onChange={(e) => { const t = templates.find((x) => String(x.id) === e.target.value); setEmailModal((p) => ({ ...p, templateId: e.target.value, bodyHtml: t?.body_html || p.bodyHtml })); }}>
                      <option value="">(Sin plantilla)</option>
                      {templates.map((t) => <option key={t.id} value={String(t.id)}>{t.name || `Plantilla #${t.id}`}</option>)}
                    </select>
                  </div>
                )}
                <div className="text-muted small">Se adjuntará el PDF del presupuesto (el archivo marcado como "PDF presupuesto" si lo hay).</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEmailModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendEmail} disabled={emailModal.sending}>{emailModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {whatsappModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enviar presupuesto {whatsappModal.budgetNumber} por WhatsApp</h5>
                <button className="btn-close" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}></button>
              </div>
              <div className="modal-body">
                {whatsappModal.error && <div className="alert alert-danger py-2">{whatsappModal.error}</div>}
                {whatsappModal.loading ? <p>Cargando datos del cliente...</p> : (
                  <>
                    <div className="mb-2"><strong>{whatsappModal.clientName}</strong></div>
                    <div className="mb-2">
                      <label className="form-label small">Teléfono</label>
                      <input className="form-control" value={whatsappModal.phone} onChange={(e) => setWhatsappModal((p) => ({ ...p, phone: e.target.value }))} placeholder="+34..." />
                    </div>
                    <div className="text-muted small">Se enviará el PDF del presupuesto (el archivo marcado como "PDF presupuesto" si lo hay) mediante la plantilla de WhatsApp configurada.</div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setWhatsappModal((p) => ({ ...p, open: false }))}>Cancelar</button>
                <button className="btn btn-primary" onClick={sendWhatsapp} disabled={whatsappModal.sending || whatsappModal.loading}>{whatsappModal.sending ? 'Enviando...' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresupuestosPage;
